import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Stack,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DevicesIcon from '@mui/icons-material/Devices';
import MainLayout from '../../../components/layout/MainLayout';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useAuth } from '../../../contexts/AuthContext';
import {
  listTouchPanels,
  createTouchPanel,
  duplicateTouchPanel,
  deleteTouchPanel,
  filterTouchPanels,
  sortTouchPanelsByUpdatedDesc,
} from '../../../services/touchPanelService';
import {
  listTouchPanelAssignments,
  listEligibleTouchPanelDevices,
} from '../../../services/touchPanelAssignmentService';
import {
  ASSIGNMENT_STATUS,
  ASSIGNMENT_STATUS_LABELS,
  computePanelDeploymentState,
} from '../../../services/touchPanelDeployable/assignmentStatus';
import AssignDevicesDialog from './AssignDevicesDialog';
import ClaimTouchPanelDialog from './ClaimTouchPanelDialog';

function formatUpdatedAt(value) {
  if (!value) return '—';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatResolution(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return `${Math.round(w)}×${Math.round(h)}`;
}

/** Short applied device id as shown on panels (MAC hex), not the layout panel_* id. */
function shortDeviceId(deviceId) {
  if (!deviceId) return null;
  const raw = String(deviceId).trim();
  const withoutAgent = raw.replace(/^agent-panel-/i, '').replace(/^panel-/i, '');
  return withoutAgent || raw;
}

function deviceIdsForAssignments(panelAssignments) {
  const labels = [];
  const seen = new Set();
  (panelAssignments || []).forEach((a) => {
    const id = shortDeviceId(a?.deviceId);
    if (id && !seen.has(id)) {
      seen.add(id);
      labels.push(id);
    }
  });
  return labels.length ? labels.join(', ') : '—';
}

function resolutionForAssignments(panelAssignments, deviceById) {
  const labels = [];
  const seen = new Set();
  (panelAssignments || []).forEach((a) => {
    const live = a?.deviceId ? deviceById.get(a.deviceId) : null;
    const snap = a?.deviceSnapshot || {};
    const label =
      formatResolution(live?.displayWidth, live?.displayHeight) ||
      formatResolution(snap.displayWidth, snap.displayHeight) ||
      formatResolution(snap.display?.width, snap.display?.height);
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  });
  return labels.length ? labels.join(', ') : '—';
}

function deploymentChipColor(state) {
  if (state === ASSIGNMENT_STATUS.DEPLOYED || state === ASSIGNMENT_STATUS.PREPARED) return 'success';
  if (
    state === ASSIGNMENT_STATUS.CHANGES_NOT_PREPARED ||
    state === ASSIGNMENT_STATUS.DEPLOYING ||
    state === ASSIGNMENT_STATUS.SENT ||
    state === ASSIGNMENT_STATUS.AWAITING_DEVICE ||
    state === ASSIGNMENT_STATUS.MIXED ||
    state === ASSIGNMENT_STATUS.TIMED_OUT
  ) {
    return 'warning';
  }
  if (
    state === ASSIGNMENT_STATUS.INCOMPATIBLE ||
    state === ASSIGNMENT_STATUS.DEPLOYMENT_FAILED
  ) {
    return 'error';
  }
  if (state === ASSIGNMENT_STATUS.ASSIGNED || state === ASSIGNMENT_STATUS.DEVICE_OFFLINE) {
    return 'info';
  }
  return 'default';
}

/**
 * Touch Panels list — Phase 3 CRUD + Phase 4 assignment indicators.
 */
export default function TouchPanelListPage() {
  const navigate = useNavigate();
  const { userData, userClaims, currentUser, tenantId: authTenantId } = useAuth();

  const tenantId = authTenantId || userData?.tenantId || userClaims?.tenantId;
  const userId = currentUser?.uid || null;
  const role = userClaims?.role || userData?.role;
  const hasAccess = role === 'admin' || role === 'tenant_admin' || role === 'superadmin';

  const [panels, setPanels] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignPanel, setAssignPanel] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);

  const loadAll = useCallback(async () => {
    if (!tenantId || !hasAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [rows, assigns, eligible] = await Promise.all([
        listTouchPanels(tenantId),
        listTouchPanelAssignments(tenantId),
        listEligibleTouchPanelDevices(tenantId, { includeIncompatible: true }).catch((err) => {
          console.warn('[TouchPanels] eligible devices load failed', err);
          return [];
        }),
      ]);
      setPanels(sortTouchPanelsByUpdatedDesc(rows));
      setAssignments(assigns || []);
      setDevices(Array.isArray(eligible) ? eligible : []);
    } catch (err) {
      console.error('[TouchPanels] list failed', err);
      setError(err.message || 'Failed to load touch panels');
      setPanels([]);
      setAssignments([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, hasAccess]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const assignmentsByPanel = useMemo(() => {
    const map = new Map();
    (assignments || []).forEach((a) => {
      if (!a?.panelId || a.enabled === false) return;
      if (!map.has(a.panelId)) map.set(a.panelId, []);
      map.get(a.panelId).push(a);
    });
    return map;
  }, [assignments]);

  const deviceById = useMemo(() => {
    const map = new Map();
    (devices || []).forEach((d) => {
      if (d?.deviceId) map.set(d.deviceId, d);
    });
    return map;
  }, [devices]);

  const enrichedRows = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    const base = filterTouchPanels(panels, search);
    const matched = new Map(base.map((p) => [p.id, p]));
    if (q) {
      panels.forEach((p) => {
        const panelAssignments = assignmentsByPanel.get(p.id) || [];
        const deviceIds = deviceIdsForAssignments(panelAssignments).toLowerCase();
        if (
          String(p.id || '').toLowerCase().includes(q) ||
          deviceIds.includes(q)
        ) {
          matched.set(p.id, p);
        }
      });
    }
    return sortTouchPanelsByUpdatedDesc([...matched.values()]).map((p) => {
      const panelAssignments = assignmentsByPanel.get(p.id) || [];
      const deploymentState = computePanelDeploymentState({
        assignments: panelAssignments,
        panelRevision: p.revision,
      });
      return {
        ...p,
        appliedDeviceId: deviceIdsForAssignments(panelAssignments),
        resolution: resolutionForAssignments(panelAssignments, deviceById),
        assignedDeviceCount: panelAssignments.length,
        assignedDeviceNames: panelAssignments
          .map((a) => a.deviceSnapshot?.name || a.deviceId)
          .filter(Boolean),
        deploymentState,
      };
    });
  }, [panels, search, assignmentsByPanel, deviceById]);

  const handleCreate = async () => {
    if (!tenantId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createTouchPanel(tenantId, userId);
      navigate(`/admin/touch-panels/${created.id}`);
    } catch (err) {
      console.error('[TouchPanels] create failed', err);
      setError(err.message || 'Failed to create panel');
      setBusy(false);
    }
  };

  const handleOpen = (panelId) => {
    navigate(`/admin/touch-panels/${panelId}`);
  };

  const handleDuplicate = async (panel) => {
    if (!tenantId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const copy = await duplicateTouchPanel(tenantId, panel.id, userId);
      await loadAll();
      navigate(`/admin/touch-panels/${copy.id}`);
    } catch (err) {
      console.error('[TouchPanels] duplicate failed', err);
      setError(err.message || 'Failed to duplicate panel');
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!tenantId || !deleteTarget) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTouchPanel(tenantId, deleteTarget.id);
      setDeleteTarget(null);
      await loadAll();
    } catch (err) {
      console.error('[TouchPanels] delete failed', err);
      if (err.code === 'PANEL_HAS_ASSIGNMENTS') {
        const names = (err.assignments || [])
          .map((a) => a.deviceSnapshot?.name || a.deviceId)
          .join(', ');
        setError(
          `Cannot delete “${deleteTarget.name}” while assigned to: ${names || 'devices'}. Unassign first.`
        );
        setDeleteTarget(null);
      } else {
        setError(err.message || 'Failed to delete panel');
      }
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.1,
      minWidth: 160,
    },
    {
      field: 'appliedDeviceId',
      headerName: 'Device ID',
      flex: 1.1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography
          variant="caption"
          sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}
        >
          {params.row.appliedDeviceId || '—'}
        </Typography>
      ),
    },
    {
      field: 'resolution',
      headerName: 'Resolution',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
          {params.row.resolution || '—'}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 140,
      valueGetter: (value, row) => row?.description || value || '',
    },
    {
      field: 'assignedDeviceCount',
      headerName: 'Assigned Devices',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          icon={<DevicesIcon />}
          label={params.row.assignedDeviceCount || 0}
          variant={params.row.assignedDeviceCount ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'revision',
      headerName: 'Revision',
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'deploymentState',
      headerName: 'Deployment State',
      width: 180,
      renderCell: (params) => {
        const state = params.row.deploymentState || ASSIGNMENT_STATUS.NOT_ASSIGNED;
        return (
          <Chip
            size="small"
            color={deploymentChipColor(state)}
            label={ASSIGNMENT_STATUS_LABELS[state] || state}
          />
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 170,
      valueGetter: (value, row) => formatUpdatedAt(row?.updatedAt ?? value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 280,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => handleOpen(params.row.id)}
          >
            Open
          </Button>
          <Button size="small" onClick={() => setAssignPanel(params.row)} disabled={busy}>
            Assign
          </Button>
          <IconButton
            size="small"
            title="Duplicate"
            disabled={busy}
            onClick={() => handleDuplicate(params.row)}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Delete"
            color="error"
            disabled={busy}
            onClick={() => {
              const assigned = assignmentsByPanel.get(params.row.id) || [];
              if (assigned.length > 0) {
                const names = assigned
                  .map((a) => a.deviceSnapshot?.name || a.deviceId)
                  .join(', ');
                setError(
                  `Cannot delete “${params.row.name}” while assigned to: ${names}. Unassign first.`
                );
                setAssignPanel(params.row);
                return;
              }
              setDeleteTarget(params.row);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  if (!hasAccess) {
    return (
      <MainLayout>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
          <Alert severity="error">You do not have permission to manage Touch Panels.</Alert>
        </Box>
      </MainLayout>
    );
  }

  if (!tenantId) {
    return (
      <MainLayout>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
          <Alert severity="warning">No tenant selected. Sign in with a tenant-scoped admin account.</Alert>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Touch Panels
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Design layouts and assign them to eligible ESP32 Edge Agents.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<DevicesIcon />} onClick={() => setClaimOpen(true)}>
              Claim Touch Panel
            </Button>
            <Button
              variant="contained"
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={handleCreate}
              disabled={busy}
            >
              New Panel
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Search by name, description, or device ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Paper>

        <Paper sx={{ height: 560, width: '100%' }}>
          <DataGrid
            rows={enrichedRows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: 'updatedAt', sort: 'desc' }] },
            }}
            getRowId={(row) => row.id}
            slots={{
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
                  <Typography color="text.secondary">No touch panels yet.</Typography>
                  <Chip label="Create one with New Panel" size="small" />
                </Stack>
              ),
            }}
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': { alignItems: 'center' },
            }}
          />
        </Paper>
      </Box>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Touch Panel"
        message={
          deleteTarget
            ? `Delete “${deleteTarget.name}”? This cannot be undone.`
            : 'Delete this touch panel?'
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <AssignDevicesDialog
        open={!!assignPanel}
        onClose={() => setAssignPanel(null)}
        tenantId={tenantId}
        userId={userId}
        panel={assignPanel}
        onChanged={loadAll}
      />

      <ClaimTouchPanelDialog
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        tenantId={tenantId}
        onClaimed={() => {
          loadAll();
        }}
      />
    </MainLayout>
  );
}
